import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import SectionLabel from '../components/SectionLabel';
import Icon from '../components/Icon';
import { fetchUpcomingMeetings, useLive } from '../data/live';
import Skeleton from '../components/Skeleton';

// Fixed council display order for the filter chips.
const COUNCIL_ORDER = ['ihes', 'cpes', 'csce', 'muia'];

// Cross-council list of upcoming IHES/CPES/CSCE/MUIA meetings — dates and
// locations pulled live from Mission Control events. A chip row filters by
// council, defaulting to the council of the portal you're currently in.
// The current meeting is marked "You are here"; others link to their own
// portal when one exists (portal_slug present).
export default function UpcomingPage() {
  const { meeting } = useOutletContext();
  const event = meeting._event;
  const currentKey = meeting.themeSlug || meeting.slug;

  const live = useLive(fetchUpcomingMeetings, [event?.id]);
  const meetings = live || [];
  const [council, setCouncil] = useState(currentKey);

  // Councils that actually have upcoming meetings, in fixed order.
  const available = COUNCIL_ORDER.filter((k) => meetings.some((m) => m.councilKey === k));

  // If the current portal's council has no upcoming meetings, fall back to All
  // so the page isn't empty on load.
  const effective = council === 'all' || available.includes(council) ? council : 'all';
  const filtered = effective === 'all'
    ? meetings
    : meetings.filter((m) => m.councilKey === effective);

  // Only show chips when there's more than one council to choose between.
  const showChips = available.length > 1;

  if (live === undefined) return <Skeleton variant="card" count={3} />;

  return (
    <>
      {showChips && (
        <div className="portal-upcoming-filter">
          <button
            type="button"
            className={`portal-upcoming-chip${effective === 'all' ? ' active' : ''}`}
            onClick={() => setCouncil('all')}
          >
            All
          </button>
          {available.map((k) => (
            <button
              key={k}
              type="button"
              className={`portal-upcoming-chip${effective === k ? ' active' : ''}`}
              onClick={() => setCouncil(k)}
            >
              {k.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <SectionLabel right={`${filtered.length}`}>Upcoming Meetings</SectionLabel>

      {filtered.length === 0 ? (
        <div className="portal-overview-body">No upcoming meetings scheduled yet.</div>
      ) : (
        <div className="portal-upcoming-list">
          {filtered.map((m) => {
            const inner = (
              <>
                <div className="portal-upcoming-date">{m.dateLabel}</div>
                <div className="portal-upcoming-name">{m.name}</div>
                {m.location && <div className="portal-upcoming-loc"><Icon name="map-pin" size={12} /> {m.location}</div>}
                <div className="portal-upcoming-meta">
                  {m.council && <span className="portal-upcoming-council">{m.council}</span>}
                  {m.isCurrent && <span className="portal-upcoming-current">You are here</span>}
                </div>
              </>
            );

            // Link to another meeting's portal only when it has a live portal
            // and it isn't the meeting you're already viewing.
            if (m.hasPortal && !m.isCurrent) {
              return (
                <Link key={m.id} to={`/${m.slug}`} className="portal-upcoming-card is-link">
                  {inner}
                </Link>
              );
            }
            return (
              <div key={m.id} className={`portal-upcoming-card${m.isCurrent ? ' is-current' : ''}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
